/* ThreadKalaa wishlist — shared API
 * ---------------------------------
 *   Storage: localStorage key `tk_wishlist`
 *   Provides: window.TKWishlist with toggle/has/list/count/clear/on
 *   Side effects:
 *     - fires `tk:wishlist:change` on document (legacy contract)
 *     - shows toast on add/remove
 *     - updates header counter bubble
 *     - syncs heart buttons across the page (aria-pressed + .saved class)
 *
 *   Backward compat:
 *     - window.tkWishlistToggle(btn, id, title, url) — used by product
 *       page snippets — now routes through this central API.
 *     - window.tkWishlist(btn, id, title, url) — same, older name.
 *
 *   On /pages/wishlist this script also fetches each saved product via
 *   Shopify's /products/{handle}.js and renders the grid in #tk-wishlist-grid.
 */
(function () {
  'use strict';

  if (window.__tkWishlistInit) return;
  window.__tkWishlistInit = true;

  var KEY = 'tk_wishlist';
  var TOAST_DURATION = 4500;

  /* ============================================================
     Storage
     ============================================================ */
  function read() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list));
    } catch (e) {
      /* quota or disabled — fail silently */
    }
  }

  function has(productId) {
    var pid = String(productId);
    return read().some(function (item) {
      return String(item.id) === pid;
    });
  }

  function findIndex(productId) {
    var pid = String(productId);
    var list = read();
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === pid) return i;
    }
    return -1;
  }

  function add(item) {
    var list = read();
    if (findIndex(item.id) > -1) return false;
    list.push({
      id: item.id,
      handle: item.handle || '',
      title: item.title || '',
      url: item.url || '',
      addedAt: Date.now(),
    });
    write(list);
    return true;
  }

  function remove(productId) {
    var idx = findIndex(productId);
    if (idx === -1) return false;
    var list = read();
    list.splice(idx, 1);
    write(list);
    return true;
  }

  function toggle(item) {
    var added;
    if (has(item.id)) {
      remove(item.id);
      added = false;
    } else {
      add(item);
      added = true;
    }
    fireChange();
    syncButtons();
    updateCounter();
    showToast(item.title, item.handle, added);
    return added;
  }

  function clearAll() {
    write([]);
    fireChange();
    syncButtons();
    updateCounter();
  }

  function count() {
    return read().length;
  }

  function list() {
    return read();
  }

  function fireChange() {
    document.dispatchEvent(
      new CustomEvent('tk:wishlist:change', { detail: { count: count() } })
    );
  }

  /* ============================================================
     Cross-tab sync — storage event
     ============================================================ */
  window.addEventListener('storage', function (e) {
    if (e.key !== KEY) return;
    syncButtons();
    updateCounter();
    if (isWishlistPage()) renderPage();
  });

  /* ============================================================
     Heart button sync
     Any button with [data-tk-wishlist] (collection cards), or the
     legacy IDs `wl-btn-{id}` / `.tk-save-cta` (product page) — gets
     its visual state synced from localStorage on init / after toggle.
     ============================================================ */
  function syncButtons() {
    var wished = read().reduce(function (acc, it) {
      acc[String(it.id)] = true;
      return acc;
    }, {});

    document.querySelectorAll('[data-tk-wishlist]').forEach(function (btn) {
      var pid = btn.getAttribute('data-product-id');
      var on = !!wished[String(pid)];
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('saved', on);
    });

    document.querySelectorAll('.tk-save-cta, .wl-btn, .tk-save-floater').forEach(function (btn) {
      var id = btn.id || '';
      var match = id.match(/^wl-btn-(\d+)/);
      if (!match) return;
      var on = !!wished[match[1]];
      btn.classList.toggle('saved', on);
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      /* Share-strip variant has a .wl-label child showing Save / Saved! */
      var label = btn.querySelector('.wl-label');
      if (label) label.textContent = on ? 'Saved!' : 'Save';
    });
  }

  /* ============================================================
     Header counter bubble
     ============================================================ */
  function updateCounter() {
    var n = count();
    document.querySelectorAll('[data-tk-wishlist-bubble]').forEach(function (el) {
      /* Empty text → Dawn's .cart-count-bubble:empty { display: none }
         rule auto-hides the bubble. Visible only when count > 0. */
      el.textContent = n > 0 ? String(n) : '';
    });
    /* Legacy hooks (some old templates may still reference these). */
    document.querySelectorAll('[data-tk-wishlist-count]').forEach(function (el) {
      el.textContent = String(n);
    });
  }

  /* ============================================================
     Toast
     ============================================================ */
  function ensureToastContainer() {
    var el = document.getElementById('tk-wishlist-toast');
    if (el) return el;
    el = document.createElement('div');
    el.id = 'tk-wishlist-toast';
    el.setAttribute('role', 'status');
    el.setAttribute('aria-live', 'polite');
    el.hidden = true;
    document.body.appendChild(el);
    return el;
  }

  var toastTimer = null;
  function showToast(title, handle, added) {
    var el = ensureToastContainer();
    var safeTitle = title || 'Item';
    var msg = added
      ? escapeHTML(safeTitle) + ' added to your wishlist'
      : escapeHTML(safeTitle) + ' removed from your wishlist';

    var html =
      '<div class="tk-toast__inner">' +
      '<svg class="tk-toast__heart" viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">' +
      '<path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" fill="' +
      (added ? '#ffffff' : 'none') +
      '" stroke="#ffffff" stroke-width="1.6"/>' +
      '</svg>' +
      '<span class="tk-toast__msg">' +
      msg +
      '</span>' +
      (added
        ? '<a class="tk-toast__cta" href="/pages/wishlist">GO TO WISHLIST</a>'
        : '') +
      '<button class="tk-toast__close" aria-label="Dismiss">' +
      '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">' +
      '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
      '</svg>' +
      '</button>' +
      '</div>';

    el.innerHTML = html;
    el.hidden = false;
    /* force reflow then animate in */
    void el.offsetWidth;
    el.classList.add('tk-toast--visible');

    var closeBtn = el.querySelector('.tk-toast__close');
    if (closeBtn) closeBtn.addEventListener('click', hideToast);

    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, TOAST_DURATION);
  }

  function hideToast() {
    var el = document.getElementById('tk-wishlist-toast');
    if (!el) return;
    el.classList.remove('tk-toast--visible');
    setTimeout(function () {
      el.hidden = true;
    }, 300);
  }

  function escapeHTML(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /* ============================================================
     Click delegation for collection cards
     (Product page snippets define their own onclick handlers that
     call into TKWishlist directly via the legacy global fns below.)
     ============================================================ */
  document.addEventListener(
    'click',
    function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[data-tk-wishlist]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      var pid = btn.getAttribute('data-product-id');
      var handle = btn.getAttribute('data-product-handle') || '';
      var title = btn.getAttribute('data-product-title') || '';
      var url = btn.getAttribute('data-product-url') || (handle ? '/products/' + handle : '');
      if (!pid) return;
      toggle({ id: pid, handle: handle, title: title, url: url });
    },
    /* useCapture */ true
  );

  /* ============================================================
     Wishlist page renderer
     ============================================================ */
  function isWishlistPage() {
    return !!document.getElementById('tk-wishlist-grid');
  }

  function renderPage() {
    var grid = document.getElementById('tk-wishlist-grid');
    var countEl = document.getElementById('tk-wishlist-count-label');
    var emptyEl = document.getElementById('tk-wishlist-empty');
    var hasItemsHeader = document.getElementById('tk-wishlist-meta');
    if (!grid) return;

    var items = read();
    if (countEl) {
      countEl.textContent = items.length === 1 ? '1 ITEM' : items.length + ' ITEMS';
    }

    if (items.length === 0) {
      grid.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      if (hasItemsHeader) hasItemsHeader.hidden = true;
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    if (hasItemsHeader) hasItemsHeader.hidden = false;

    /* Initial skeleton — replaced as each product resolves */
    grid.innerHTML = items
      .map(function (it) {
        return (
          '<div class="tk-wl-card tk-wl-card--loading" data-product-id="' +
          escapeHTML(it.id) +
          '" data-product-handle="' +
          escapeHTML(it.handle) +
          '"><div class="tk-wl-card__skeleton"></div></div>'
        );
      })
      .join('');

    items.forEach(function (it) {
      if (!it.handle) {
        replaceCard(it.id, renderUnknownCard(it));
        return;
      }
      fetch('/products/' + it.handle + '.js', { credentials: 'same-origin' })
        .then(function (r) {
          if (!r.ok) throw new Error('bad response');
          return r.json();
        })
        .then(function (product) {
          replaceCard(it.id, renderProductCard(product, it));
        })
        .catch(function () {
          replaceCard(it.id, renderUnknownCard(it));
        });
    });
  }

  function replaceCard(id, html) {
    var grid = document.getElementById('tk-wishlist-grid');
    if (!grid) return;
    var card = grid.querySelector('[data-product-id="' + cssEscape(id) + '"]');
    if (!card) return;
    var wrap = document.createElement('div');
    wrap.innerHTML = html;
    var newCard = wrap.firstElementChild;
    if (newCard) card.replaceWith(newCard);
  }

  function cssEscape(s) {
    return String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function renderProductCard(product, wishItem) {
    var img =
      product.featured_image ||
      (product.images && product.images[0]) ||
      '';
    var imgSrc = img ? img.replace(/(\.[a-z]+)(\?|$)/, '_600x$1$2') : '';
    var price = product.price; // in cents (paise for INR)
    var compareAt = product.compare_at_price;
    var firstVariant =
      product.variants && product.variants.length
        ? product.variants[0]
        : null;
    var variantId = firstVariant ? firstVariant.id : '';
    var available = firstVariant ? firstVariant.available : false;
    var multipleVariants =
      product.variants && product.variants.length > 1;

    return (
      '<div class="tk-wl-card" data-product-id="' +
      escapeHTML(product.id) +
      '" data-product-handle="' +
      escapeHTML(product.handle) +
      '">' +
      '<button class="tk-wl-card__remove" aria-label="Remove from wishlist" data-tk-wl-remove="' +
      escapeHTML(product.id) +
      '">' +
      '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">' +
      '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
      '</svg>' +
      '</button>' +
      '<a class="tk-wl-card__media" href="/products/' +
      escapeHTML(product.handle) +
      '">' +
      (imgSrc
        ? '<img src="' +
          escapeHTML(imgSrc) +
          '" alt="' +
          escapeHTML(product.title) +
          '" loading="lazy">'
        : '<div class="tk-wl-card__noimg"></div>') +
      '</a>' +
      '<div class="tk-wl-card__body">' +
      '<a class="tk-wl-card__title" href="/products/' +
      escapeHTML(product.handle) +
      '">' +
      escapeHTML(product.title) +
      '</a>' +
      '<div class="tk-wl-card__price">' +
      (compareAt && compareAt > price
        ? '<span class="tk-wl-card__price-now">' +
          formatMoney(price) +
          '</span>' +
          '<span class="tk-wl-card__price-was">' +
          formatMoney(compareAt) +
          '</span>'
        : '<span class="tk-wl-card__price-now">' +
          formatMoney(price) +
          '</span>') +
      '</div>' +
      (available
        ? (multipleVariants
            ? '<a class="tk-wl-card__cta" href="/products/' +
              escapeHTML(product.handle) +
              '">SELECT OPTIONS</a>'
            : '<button class="tk-wl-card__cta" data-tk-wl-add="' +
              escapeHTML(variantId) +
              '">ADD TO BAG</button>')
        : '<button class="tk-wl-card__cta tk-wl-card__cta--soldout" disabled>SOLD OUT</button>') +
      '</div>' +
      '</div>'
    );
  }

  function renderUnknownCard(wishItem) {
    return (
      '<div class="tk-wl-card tk-wl-card--unknown" data-product-id="' +
      escapeHTML(wishItem.id) +
      '">' +
      '<button class="tk-wl-card__remove" aria-label="Remove from wishlist" data-tk-wl-remove="' +
      escapeHTML(wishItem.id) +
      '">' +
      '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">' +
      '<path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
      '</svg>' +
      '</button>' +
      '<div class="tk-wl-card__body">' +
      '<a class="tk-wl-card__title" href="' +
      escapeHTML(wishItem.url || '#') +
      '">' +
      escapeHTML(wishItem.title || 'Saved item') +
      '</a>' +
      '<p class="tk-wl-card__unknown-msg">This product is no longer available.</p>' +
      '</div>' +
      '</div>'
    );
  }

  function formatMoney(cents) {
    var rupees = Math.round(cents / 100);
    return '₹ ' + rupees.toLocaleString('en-IN');
  }

  /* Wishlist page click handling (remove + add-to-bag) */
  document.addEventListener('click', function (e) {
    var remove = e.target && e.target.closest && e.target.closest('[data-tk-wl-remove]');
    if (remove) {
      e.preventDefault();
      var id = remove.getAttribute('data-tk-wl-remove');
      removeFromPage(id);
      return;
    }
    var addBtn =
      e.target && e.target.closest && e.target.closest('[data-tk-wl-add]');
    if (addBtn) {
      e.preventDefault();
      var variantId = addBtn.getAttribute('data-tk-wl-add');
      addVariantToCart(variantId, addBtn);
      return;
    }
    var clearBtn = e.target && e.target.closest && e.target.closest('#tk-wishlist-clear');
    if (clearBtn) {
      e.preventDefault();
      if (window.confirm('Remove all items from your wishlist?')) clearAll();
    }
  });

  function removeFromPage(id) {
    var item = read().find(function (it) {
      return String(it.id) === String(id);
    });
    var title = item ? item.title : '';
    remove(id);
    fireChange();
    syncButtons();
    updateCounter();
    showToast(title, item && item.handle, false);
    /* Re-render the page list */
    renderPage();
  }

  function addVariantToCart(variantId, btn) {
    if (!variantId || !btn) return;
    if (btn.classList.contains('is-loading')) return;
    btn.classList.add('is-loading');

    var formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);
    formData.append('sections', 'cart-icon-bubble');

    var routes = (window.Shopify && window.Shopify.routes) || {};
    var addUrl = (routes.root || '/') + 'cart/add.js';
    addUrl = addUrl.replace('//', '/');

    fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript' },
      body: formData,
    })
      .then(function (r) {
        return r.json().then(function (data) {
          return { ok: r.ok, data: data };
        });
      })
      .then(function (res) {
        btn.classList.remove('is-loading');
        if (!res.ok) {
          btn.animate(
            [
              { transform: 'translateX(0)' },
              { transform: 'translateX(-3px)' },
              { transform: 'translateX(3px)' },
              { transform: 'translateX(0)' },
            ],
            { duration: 280 }
          );
          return;
        }
        btn.classList.add('is-success');
        btn.textContent = 'ADDED ✓';
        setTimeout(function () {
          btn.classList.remove('is-success');
          btn.textContent = 'ADD TO BAG';
        }, 1800);
        if (
          res.data &&
          res.data.sections &&
          res.data.sections['cart-icon-bubble']
        ) {
          updateCartBubble(res.data.sections['cart-icon-bubble']);
        }
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          try {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'tk-wishlist',
              productVariantId: variantId,
              cartData: res.data,
            });
          } catch (e) {
            /* ignore */
          }
        }
      })
      .catch(function () {
        btn.classList.remove('is-loading');
      });
  }

  function updateCartBubble(htmlString) {
    try {
      var parser = new DOMParser();
      var doc = parser.parseFromString(htmlString, 'text/html');
      var fresh = doc.querySelector('.cart-count-bubble');
      var current = document.querySelector(
        '#cart-icon-bubble .cart-count-bubble'
      );
      if (!fresh) return;
      if (current) current.replaceWith(fresh);
      else {
        var cartIcon = document.getElementById('cart-icon-bubble');
        if (cartIcon) cartIcon.appendChild(fresh);
      }
    } catch (e) {
      /* ignore */
    }
  }

  /* ============================================================
     Backward-compat global functions
     Existing snippets use tkWishlistToggle / tkWishlist —
     route them through the central API.
     ============================================================ */
  window.tkWishlistToggle = function (btn, id, title, url) {
    var handle = '';
    var m = String(url || '').match(/\/products\/([^\/?#]+)/);
    if (m) handle = m[1];
    toggle({ id: id, title: title, url: url, handle: handle });
  };
  window.tkWishlist = window.tkWishlistToggle;

  /* ============================================================
     Public API
     ============================================================ */
  window.TKWishlist = {
    toggle: toggle,
    add: add,
    remove: function (id) {
      remove(id);
      fireChange();
      syncButtons();
      updateCounter();
    },
    has: has,
    list: list,
    count: count,
    clear: clearAll,
  };

  /* ============================================================
     Boot
     ============================================================ */
  function boot() {
    syncButtons();
    updateCounter();
    if (isWishlistPage()) renderPage();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
